/** TEK_RSI_MOMENTUM backtest trade verification: read-only data + the manual review annotation. */
import api from "@/services/api";
import type {
  RsiBacktestRunSummary,
  RsiBacktestTradeDetail,
  RsiBacktestTradeFilters,
  RsiBacktestTradeList,
  RsiMomentumBacktestResult,
  RsiTradeReview,
  RsiVerificationStatus,
} from "@/types";

const BASE = "/admin/strategies/rsi-momentum/backtest";

const noPaging = ({ page: _page, page_size: _size, ...rest }: RsiBacktestTradeFilters): RsiBacktestTradeFilters => { void _page; void _size; return rest; };
const clean = (p: object) => Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== "" && v !== null));

async function download(path: string, params: object, filename: string): Promise<void> {
  // authenticated API call (a plain <a href> would 401) -> Blob -> browser download
  const response = await api.get(path, { params: clean(params), responseType: "blob" });
  const g = globalThis;
  const url = g.URL.createObjectURL(response.data);
  const link = g.document.createElement("a");
  link.href = url;
  link.download = filename;
  g.document.body.appendChild(link);
  link.click();
  g.document.body.removeChild(link);
  g.URL.revokeObjectURL(url);
}

export const rsiBacktestVerificationService = {
  async listRuns(limit = 50): Promise<RsiBacktestRunSummary[]> {
    const { data } = await api.get<RsiBacktestRunSummary[]>(`${BASE}/runs`, { params: { limit } });
    return data;
  },
  async getRun(id: string): Promise<RsiMomentumBacktestResult> {
    const { data } = await api.get<RsiMomentumBacktestResult>(`${BASE}/${id}`);
    return data;
  },
  async listTrades(id: string, filters: RsiBacktestTradeFilters): Promise<RsiBacktestTradeList> {
    const { data } = await api.get<RsiBacktestTradeList>(`${BASE}/${id}/trades`, { params: clean(filters) });
    return data;
  },
  async getTrade(id: string, no: number): Promise<RsiBacktestTradeDetail> {
    const { data } = await api.get<RsiBacktestTradeDetail>(`${BASE}/${id}/trades/${no}`);
    return data;
  },
  /** Same shape the Trade Review chart consumes. */
  async getTradeCandles(id: string, no: number): Promise<RsiTradeReview> {
    const { data } = await api.get<RsiTradeReview>(`${BASE}/${id}/trades/${no}/candles`);
    return data;
  },
  async saveVerification(id: string, no: number, status: RsiVerificationStatus, note: string | null) {
    const { data } = await api.post(`${BASE}/${id}/trades/${no}/verification`, { status, note });
    return data as { trade_no: number; status: RsiVerificationStatus; note: string | null; updated_at: string };
  },
  exportTrades(id: string, filters: RsiBacktestTradeFilters, label: string) {
    return download(`${BASE}/${id}/trades/export`, noPaging(filters), `rsi-backtest-${id.slice(0, 8)}-trades-${label}.csv`);
  },
  exportCandles(id: string, filters: RsiBacktestTradeFilters, label: string) {
    return download(`${BASE}/${id}/trades/candles/export`, noPaging(filters), `rsi-backtest-${id.slice(0, 8)}-candles-${label}.csv`);
  },
  exportTradeCandles(id: string, no: number) {
    return download(`${BASE}/${id}/trades/${no}/candles/export`, {}, `rsi-backtest-${id.slice(0, 8)}-trade-${no}-candles.csv`);
  },
};

export default rsiBacktestVerificationService;
